<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import DescriptionBlock from './description-block.vue';
import CustomButton from './custom-button.vue';
import StoryCard from './story-card.vue';
import styles from './stories-block-style.scss';

const b = bem('stories-block', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'StoriesBlock',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    EditButtonModal,
    EditTitleModal,
    ImageUploadModal,
    TitleWithLinesBlock,
    DescriptionBlock,
    CustomButton,
    StoryCard,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      titleModalVisible: false,
      descriptionModalVisible: false,
      buttonModalVisible: false,
      buttonCommentModalVisible: false,
      // Card editing
      editingCardIndex: null,
      cardTitleModalVisible: false,
      cardImageModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    isMobile() {
      return this.isMobileView;
    },
    title() {
      return this.data.title || [];
    },
    description() {
      return this.data.description || '';
    },
    descriptionMobile() {
      return this.data.descriptionMobile || this.description;
    },
    displayDescription() {
      return this.isMobile ? this.descriptionMobile : this.description;
    },
    hasDescription() {
      return !!this.description;
    },
    dataCards() {
      return this.data.dataCards || [];
    },
    buttonText() {
      return this.data.buttonText || '';
    },
    buttonTextMobile() {
      return this.data.buttonTextMobile || this.buttonText;
    },
    displayButtonText() {
      return this.isMobile ? this.buttonTextMobile : this.buttonText;
    },
    buttonLink() {
      return this.data.buttonLink || '';
    },
    hasButton() {
      return !!this.buttonText;
    },
    buttonComment() {
      return this.data.buttonComment || '';
    },
    buttonCommentMobile() {
      return this.data.buttonCommentMobile || this.buttonComment;
    },
    displayButtonComment() {
      return this.isMobile ? this.buttonCommentMobile : this.buttonComment;
    },
    hasButtonComment() {
      return !!this.buttonComment;
    },
    // Current editing card data
    editingCard() {
      if (this.editingCardIndex === null) return null;
      return this.dataCards[this.editingCardIndex] || null;
    },
  },
  methods: {
    // Modal open methods
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.buttonModalVisible = true;
      }
    },
    openButtonCommentModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.buttonCommentModalVisible = true;
      }
    },
    openCardTitleModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingCardIndex = index;
        this.cardTitleModalVisible = true;
      }
    },
    openCardImageModal(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.editingCardIndex = index;
        this.cardImageModalVisible = true;
      }
    },
    // Save handlers
    handleTitleSave(values) {
      this.$emit('update', { title: values });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', { title: [] });
      this.titleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', { description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    handleButtonSave(values) {
      this.$emit('update', {
        buttonText: values.buttonText,
        buttonTextMobile: values.buttonTextMobile,
        buttonLink: values.buttonLink,
      });
      this.buttonModalVisible = false;
    },
    handleButtonDelete() {
      this.$emit('update', {
        buttonText: '',
        buttonTextMobile: '',
        buttonLink: '',
      });
      this.buttonModalVisible = false;
    },
    handleButtonCommentSave(values) {
      this.$emit('update', {
        buttonComment: values.description,
        buttonCommentMobile: values.descriptionMobile,
      });
      this.buttonCommentModalVisible = false;
    },
    handleButtonCommentDelete() {
      this.$emit('update', { buttonComment: '', buttonCommentMobile: '' });
      this.buttonCommentModalVisible = false;
    },
    handleCardTitleSave(values) {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        title: values.title,
        titleMobile: values.titleMobile,
      };
      this.$emit('update', { dataCards: newCards });
      this.cardTitleModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardTitleDelete() {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        title: '',
        titleMobile: '',
      };
      this.$emit('update', { dataCards: newCards });
      this.cardTitleModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardImageSave(values) {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        imageUrl: values.image || newCards[this.editingCardIndex].imageUrl,
        imageUrlMobile: values.imageMobile || newCards[this.editingCardIndex].imageUrlMobile,
      };
      this.$emit('update', { dataCards: newCards });
      this.cardImageModalVisible = false;
      this.editingCardIndex = null;
    },
    handleCardImageDelete() {
      if (this.editingCardIndex === null) return;
      const newCards = [...this.dataCards];
      newCards[this.editingCardIndex] = {
        ...newCards[this.editingCardIndex],
        imageUrl: '',
        imageUrlMobile: '',
      };
      this.$emit('update', { dataCards: newCards });
      this.cardImageModalVisible = false;
      this.editingCardIndex = null;
    },
  },
};
</script>

<template>
  <article :class="b({ mix: className, isMobileView, isEditMode })">
    <!-- Title (always present) -->
    <div
      :class="b('main-title-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
      @keyup.space="openTitleModal"
    >
      <TitleWithLinesBlock
        v-if="title.length"
        :class="b('main-title', { isMobileView })"
        :is-mobile="isMobile"
        :is-mobile-view="isMobileView"
        :text-list="title"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add title
      </div>
      <PencilIcon
        v-if="isEditMode && title.length"
        :class="b('edit-icon')"
        color="#ffffff"
        :size="20"
      />
    </div>

    <!-- Description (optional) -->
    <div
      v-if="hasDescription || isEditMode"
      :class="b('description-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openDescriptionModal"
      @keyup.enter="openDescriptionModal"
      @keyup.space="openDescriptionModal"
    >
      <DescriptionBlock
        v-if="hasDescription"
        :class="b('description', { isMobileView })"
        :is-white="true"
        :is-mobile-view="isMobileView"
      >
        {{ displayDescription }}
      </DescriptionBlock>
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add description
      </div>
      <PencilIcon
        v-if="isEditMode && hasDescription"
        :class="b('edit-icon')"
        color="#ffffff"
        :size="18"
      />
    </div>

    <!-- Story Cards (always 3 cards) -->
    <div :class="b('cards-wrap', { isMobileView })">
      <StoryCard
        v-for="(card, index) in dataCards"
        :key="card.id"
        :item="card"
        :is-mobile-view="isMobileView"
        :is-edit-mode="isEditMode"
        @edit-image="openCardImageModal(index)"
        @edit-title="openCardTitleModal(index)"
      />
    </div>

    <!-- Button (optional) -->
    <div
      v-if="hasButton || isEditMode"
      :class="b('button-wrap', { editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openButtonModal"
      @keyup.enter="openButtonModal"
      @keyup.space="openButtonModal"
    >
      <CustomButton
        v-if="hasButton"
        :class="b('button', { isMobileView })"
        :button-link="buttonLink"
        :with-line="isMobile"
        :is-mobile-view="isMobileView"
      >
        {{ displayButtonText }}
        <PencilIcon
          v-if="isEditMode"
          :class="b('edit-icon', { inline: true })"
          color="#293241"
          :size="16"
        />
      </CustomButton>
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add button
      </div>
    </div>

    <!-- Button Comment (optional) -->
    <div
      v-if="(hasButtonComment || isEditMode) && !isMobileView"
      :class="b('button-comment-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openButtonCommentModal"
      @keyup.enter="openButtonCommentModal"
      @keyup.space="openButtonCommentModal"
    >
      <div
        v-if="hasButtonComment"
        :class="b('button-comment', { isMobileView })"
      >
        {{ displayButtonComment }}
      </div>
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add comment for button
      </div>
      <PencilIcon
        v-if="isEditMode && hasButtonComment"
        :class="b('edit-icon')"
        color="#ffffff"
        :size="18"
      />
    </div>

    <!-- Modals -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="title"
      :show-delete="title.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <EditButtonModal
      :visible="buttonModalVisible"
      :button-text="buttonText"
      :button-text-mobile="buttonTextMobile"
      :button-link="buttonLink"
      @close="buttonModalVisible = false"
      @save="handleButtonSave"
      @delete="handleButtonDelete"
    />

    <!-- Button Comment Modal -->
    <EditDescriptionModal
      :visible="buttonCommentModalVisible"
      :description="buttonComment"
      :description-mobile="buttonCommentMobile"
      title="Edit Button Comment"
      @close="buttonCommentModalVisible = false"
      @save="handleButtonCommentSave"
      @delete="handleButtonCommentDelete"
    />

    <!-- Card Title Modal -->
    <EditTitleModal
      :visible="cardTitleModalVisible"
      :title="editingCard?.title || ''"
      :title-mobile="editingCard?.titleMobile || ''"
      :modal-title="`Edit Card ${editingCardIndex !== null ? editingCardIndex + 1 : ''} Title`"
      @close="cardTitleModalVisible = false; editingCardIndex = null"
      @save="handleCardTitleSave"
      @delete="handleCardTitleDelete"
    />

    <!-- Card Image Modal -->
    <ImageUploadModal
      :visible="cardImageModalVisible"
      :image="editingCard?.imageUrl || ''"
      :image-mobile="editingCard?.imageUrlMobile || ''"
      :show-delete="!!editingCard?.imageUrl"
      @close="cardImageModalVisible = false; editingCardIndex = null"
      @save="handleCardImageSave"
      @delete="handleCardImageDelete"
    />
  </article>
</template>
